import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Sparkles, ReceiptText, User, Menu, Eye, BookOpen, Handshake, ChevronRight, X } from 'lucide-react';
import './style/BottomNav.css';

/* ================================================================
   BottomNav — phone-width navigation (hidden ≥ 768px, where the
   top Nav takes over). Mirrors the customer app's tabs:
   Home · Moments · Orders · Profile · More.

   • Active tab: Material-style pill behind the icon + bold label
   • Thumb-sized targets (≥ 48px), safe-area aware (iPhone home bar)
   • Hides while the on-screen keyboard is open so it never covers
     the field being typed into
   • "New" dot on freshly launched tabs until first visit
   • "More" opens an accessible bottom sheet (Esc / overlay closes)
================================================================ */

const TABS = [
  { key: 'home',    label: 'Home',    to: '/',             Icon: Home,        match: ['/', '/moredetails', '/orderpage', '/cafemedia'], exact: ['/'] },
  { key: 'moments', label: 'Moments', to: '/happymoments', Icon: Sparkles,    match: ['/happymoments'], highlight: true },
  { key: 'orders',  label: 'Orders',  to: '/myorders',     Icon: ReceiptText, match: ['/myorders', '/orderstatus', '/invoice'] },
  { key: 'profile', label: 'Profile', to: '/myprofile',    Icon: User,        match: ['/myprofile', '/user'] },
];

const MORE_LINKS = [
  { key: 'vision',  label: 'Our vision',       sub: 'Why we’re building RestroPilots', to: '/about',   Icon: Eye },
  { key: 'blog',    label: 'Stories & blogs',  sub: 'Food guides and discoveries',     to: '/blog',    Icon: BookOpen },
  { key: 'partner', label: 'Partner with us',  sub: 'List your restaurant for free',   to: '/partner', Icon: Handshake },
];

const SEEN_KEY = 'hm_nav_seen_highlights';
const loadSeen = () => {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]')); } catch { return new Set(); }
};
const saveSeen = (set) => {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...set])); } catch { /* private mode */ }
};

const isTabActive = (tab, path) => {
  const p = path.toLowerCase();
  if (tab.exact?.includes(p)) return true;
  return tab.match.some((m) => m !== '/' && (p === m || p.startsWith(`${m}/`) || p.startsWith(`${m}?`)));
};

// A tiny tap confirmation on Android phones; skipped when the user prefers reduced motion
const tick = () => {
  try {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    if (window.matchMedia?.('(pointer: coarse)').matches) navigator.vibrate?.(6);
  } catch { /* unsupported */ }
};

const BottomNav = () => {
  const { pathname } = useLocation();
  const [seen, setSeen] = useState(loadSeen);
  const [moreOpen, setMoreOpen] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const sheetRef = useRef(null);
  const moreBtnRef = useRef(null);

  const markSeen = useCallback((key) => {
    setSeen((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      saveSeen(next);
      return next;
    });
  }, []);

  // Close the sheet on navigation
  useEffect(() => { setMoreOpen(false); }, [pathname]);

  // Sheet: Esc closes, focus moves into the sheet and back to "More" afterwards
  useEffect(() => {
    if (!moreOpen) return undefined;
    const btn = moreBtnRef.current;
    const onKey = (e) => { if (e.key === 'Escape') setMoreOpen(false); };
    window.addEventListener('keydown', onKey);
    sheetRef.current?.querySelector('a, button')?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      btn?.focus();
    };
  }, [moreOpen]);

  // Hide the bar while typing (on-screen keyboard) so it never covers inputs
  useEffect(() => {
    const isField = (el) => el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)
      && !['checkbox', 'radio', 'button', 'submit', 'range', 'file'].includes(el.type);
    const coarse = window.matchMedia?.('(pointer: coarse)').matches;
    if (!coarse) return undefined;
    const onIn = (e) => { if (isField(e.target)) setKeyboardOpen(true); };
    const onOut = () => setTimeout(() => setKeyboardOpen(isField(document.activeElement)), 50);
    document.addEventListener('focusin', onIn);
    document.addEventListener('focusout', onOut);
    return () => { document.removeEventListener('focusin', onIn); document.removeEventListener('focusout', onOut); };
  }, []);

  const moreActive = MORE_LINKS.some((l) => pathname.toLowerCase().startsWith(l.to));

  return (
    <>
      <nav className={`bn-bar ${keyboardOpen ? 'bn-bar--hidden' : ''}`} aria-label="Main">
        {TABS.map((tab) => {
          const active = isTabActive(tab, pathname);
          const showNew = tab.highlight && !seen.has(tab.key);
          return (
            <Link
              key={tab.key}
              to={tab.to}
              className={`bn-item ${active ? 'bn-item--active' : ''}`}
              aria-current={active ? 'page' : undefined}
              onClick={() => { tick(); if (showNew) markSeen(tab.key); if (active) window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            >
              <span className="bn-pill" aria-hidden="true">
                <tab.Icon size={22} strokeWidth={active ? 2.4 : 1.9} />
                {showNew && <span className="bn-dot" />}
              </span>
              <span className="bn-label">{tab.label}</span>
              {showNew && <span className="bn-sr">New</span>}
            </Link>
          );
        })}

        <button
          ref={moreBtnRef}
          type="button"
          className={`bn-item ${moreActive || moreOpen ? 'bn-item--active' : ''}`}
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
          onClick={() => { tick(); setMoreOpen((o) => !o); }}
        >
          <span className="bn-pill" aria-hidden="true">
            <Menu size={22} strokeWidth={moreActive || moreOpen ? 2.4 : 1.9} />
          </span>
          <span className="bn-label">More</span>
        </button>
      </nav>

      {moreOpen && (
        <>
          <div className="bn-overlay" onClick={() => setMoreOpen(false)} />
          <div className="bn-sheet" role="dialog" aria-modal="true" aria-label="More" ref={sheetRef}>
            <div className="bn-grabber" aria-hidden="true" />
            <div className="bn-sheet-head">
              <strong>More</strong>
              <button type="button" className="bn-close" onClick={() => setMoreOpen(false)} aria-label="Close"><X size={18} /></button>
            </div>
            <ul className="bn-list">
              {MORE_LINKS.map((l) => {
                const active = pathname.toLowerCase().startsWith(l.to);
                return (
                  <li key={l.key}>
                    <Link to={l.to} className={`bn-row ${active ? 'bn-row--active' : ''}`} aria-current={active ? 'page' : undefined} onClick={tick}>
                      <span className="bn-row-icon"><l.Icon size={19} /></span>
                      <span className="bn-row-text">
                        <span className="bn-row-title">{l.label}</span>
                        <span className="bn-row-sub">{l.sub}</span>
                      </span>
                      <ChevronRight size={18} className="bn-row-chevron" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}
    </>
  );
};

export default BottomNav;
